'use client';

import { type ComponentPropsWithoutRef, type ElementRef, forwardRef, memo, useEffect, useState } from 'react';

import * as SelectPrimitive from '@radix-ui/react-select';

import { cn } from '@/lib/utils';
import { Icon } from '@/shared/ui/icon/icon';
import type { IconName } from '@/shared/ui/icon/icon';

import { inputClasses } from './input';

const Select = SelectPrimitive.Root;

// const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = forwardRef<
    ElementRef<typeof SelectPrimitive.Trigger>,
    ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
        label?: string;
        labelClassName?: string;
        iconName?: IconName;
        iconClassName?: string;
        invalid?: boolean;
        containerClassName?: string;
    }
>(
    (
        { className, containerClassName, children, label, labelClassName, iconName, iconClassName, invalid, ...props },
        ref,
    ) => {
        const id = props.id || 'select-' + Math.random().toString(36);

        return (
            <div className={cn('relative w-full', containerClassName)}>
                {label && (
                    <label
                        htmlFor={id}
                        className={cn(
                            'text-body-xs-400 text-subtle absolute top-2 left-3 transition-all',
                            labelClassName,
                        )}>
                        {label}
                    </label>
                )}

                {/* TODO: replace hardcoded string */}
                <SelectPrimitive.Trigger
                    ref={ref}
                    id={id}
                    aria-labelledby={label ? id : undefined}
                    aria-label={label || 'Select an option'}
                    className={cn(
                        inputClasses,
                        'peer flex w-full items-center justify-between px-5 pt-2 pb-2 [&>span]:line-clamp-1',
                        { 'pt-5': label },
                        { 'outline-stroke-invalid!': invalid },
                        className,
                    )}
                    {...props}>
                    <span className='sr-only'>{label}</span>
                    {children}
                    <SelectPrimitive.Icon asChild>
                        <Icon
                            name={iconName || 'IcChevronDown'}
                            className={cn('h-4 w-4 cursor-pointer opacity-50', iconClassName)}
                        />
                    </SelectPrimitive.Icon>
                </SelectPrimitive.Trigger>
            </div>
        );
    },
);
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = forwardRef<
    ElementRef<typeof SelectPrimitive.ScrollUpButton>,
    ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.ScrollUpButton
        ref={ref}
        className={cn('flex cursor-default items-center justify-center py-1', className)}
        {...props}>
        <Icon name='IcChevronUp' className='h-4 w-4' />
    </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = forwardRef<
    ElementRef<typeof SelectPrimitive.ScrollDownButton>,
    ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.ScrollDownButton
        ref={ref}
        className={cn('flex cursor-default items-center justify-center py-1', className)}
        {...props}>
        <Icon name='IcChevronDown' className='h-4 w-4' />
    </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = forwardRef<
    ElementRef<typeof SelectPrimitive.Content>,
    ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
    <SelectPrimitive.Portal>
        <SelectPrimitive.Content
            ref={ref}
            className={cn(
                inputClasses,

                'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 h-fit max-h-96 min-w-[8rem] overflow-hidden px-0 shadow-[0px_4px_24px_0px_rgba(0,0,0,0.16)]',
                position === 'popper' &&
                    'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
                className,
            )}
            position={position}
            {...props}>
            <SelectScrollUpButton />
            <SelectPrimitive.Viewport
                className={cn(
                    'p-1',
                    position === 'popper' &&
                        'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
                )}>
                {children}
            </SelectPrimitive.Viewport>
            <SelectScrollDownButton />
        </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = forwardRef<
    ElementRef<typeof SelectPrimitive.Label>,
    ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.Label ref={ref} className={cn('py-1.5 pr-2 pl-8 text-sm font-semibold', className)} {...props} />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = forwardRef<
    ElementRef<typeof SelectPrimitive.Item>,
    ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
    <SelectPrimitive.Item
        ref={ref}
        className={cn(
            'focus:bg-surface-gray-lightest relative flex cursor-default items-center justify-start rounded-sm px-5 py-3 outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
            '[&>span]:text-body-md-400 data-[state="checked"]:[&>span]:bold-link-on-active [&>span]:bold-link-on-hover hover:[&>span]:bold-link-on-active [&>span]:relative [&>span]:text-start [&>span]:before:text-start',
            className,
        )}
        {...props}>
        <SelectPrimitive.ItemText data-text={children}>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = forwardRef<
    ElementRef<typeof SelectPrimitive.Separator>,
    ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.Separator ref={ref} className={cn('bg-muted -mx-1 my-1 h-px', className)} {...props} />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

const SelectGroup = forwardRef<
    ElementRef<typeof SelectPrimitive.SelectGroup>,
    ComponentPropsWithoutRef<typeof SelectPrimitive.SelectGroup>
>(({ className, ...props }, ref) => (
    <SelectPrimitive.SelectGroup ref={ref} className={cn('py-3', className)} {...props} />
));
SelectGroup.displayName = SelectPrimitive.SelectGroup.displayName;

export {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectScrollDownButton,
    SelectScrollUpButton,
    SelectSeparator,
    SelectTrigger,
    SelectValue,
};

const SelectItemOptimized = memo(
    ({ label, value, className }: { label: string; value: string; className?: string }) => (
        <SelectItem value={value} className={className}>
            {label}
        </SelectItem>
    ),
);

SelectItemOptimized.displayName = 'SelectItemsOptimized';

const InputSelect = forwardRef<
    ElementRef<typeof SelectPrimitive.Trigger>,
    Omit<ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>, 'onChange'> & {
        label?: string;
        labelClassName?: string;
        iconName?: IconName;
        iconClassName?: string;
        optionClassName?: string;
        containerClassName?: string;
        invalid?: boolean;
        placeholder?: string;
        options:
            | {
                  value: string;
                  label: string;
              }[]
            | null;
        value?: string;
        onChange?: (value: string) => void;
        side?: 'top' | 'right' | 'bottom' | 'left';
        align?: 'center' | 'start' | 'end';
        emptyOptionLabel?: string;
    }
>(({ placeholder, options, onChange, value, side, align, optionClassName, emptyOptionLabel, ...props }, ref) => {
    const [val, setValue] = useState<string | undefined>(value);

    useEffect(() => {
        if (value !== val) {
            setValue(value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const onSelect = (value?: string) => {
        if (!value) {
            return; // For some odd reason this cause a bug. Investigate o implement separate reset for '' case
        }

        if (value === 'unselect') {
            value = '';
        }

        setValue(value);

        if (onChange) {
            onChange(value as string);
        }
    };

    return (
        <Select defaultValue={value} onValueChange={onSelect} value={val}>
            <SelectTrigger
                iconClassName='size-5'
                className={cn('[&>span]:text-start', { '[&>span]:text-gray-600': !val })}
                {...props}
                ref={ref}>
                <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent side={side} align={align}>
                <SelectGroup>
                    {emptyOptionLabel && (
                        <SelectItem value={'unselect'} className={cn('text-gray-600', optionClassName)}>
                            {emptyOptionLabel}
                        </SelectItem>
                    )}

                    {options?.map(op => (
                        <SelectItemOptimized
                            key={op.value}
                            label={op.label}
                            value={op.value}
                            className={optionClassName}
                        />
                    ))}
                </SelectGroup>
            </SelectContent>
        </Select>
    );
});
InputSelect.displayName = 'InputSelect';

export { InputSelect };
